import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "common/locales";
import { useNavigate } from "react-router-dom";
import type { FormErrors, FormValues } from "./PasswordResetDialog";
interface Props {
  values: FormValues;
  errors: FormErrors;
  handleChange: (e: React.ChangeEvent<Element>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  passwordUpdateError: boolean;
  passwordResetInProgress: boolean;
  hasResetToken: boolean;
}

export default function PasswordResetUserDetails({
  values,
  handleChange,
  handleSubmit,
  errors,
  passwordUpdateError,
  passwordResetInProgress,
  hasResetToken,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirmPasswordError =
    !!values.password && !!values.confirmPassword && !!errors.confirmPassword; // only if password and confirm password fields have some text

  return (
    <>
      <StyledDialogTitle id="dialog-title">
        {t("passwordReset.userDetails.dialogTitle")}
      </StyledDialogTitle>
      <StyledDialogContent>
        <StyledTypography paragraph>
          {t("passwordReset.userDetails.text1")}
        </StyledTypography>

        {!hasResetToken && (
          <Typography color="error">
            {t("passwordReset.linkInvalid")}
          </Typography>
        )}
        {!hasResetToken && (
          <Button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.delete("resetToken");
              window.history.replaceState(window.history.state, "", url);
              navigate("/login/password-forgot");
            }}
          >
            {t("passwordReset.requestNewLink")}
          </Button>
        )}
        <StyledForm id="form-part-1" onSubmit={handleSubmit}>
          <TextField
            variant="standard"
            required
            id="password"
            value={values.password}
            onChange={handleChange}
            label={t("signup.password.label")}
            type="password"
            autoFocus={true}
          />
          <TextField
            variant="standard"
            required
            id="confirmPassword"
            value={values.confirmPassword}
            onChange={handleChange}
            label={t("signup.confirmPassword.label")}
            type="password"
            error={confirmPasswordError}
            helperText={confirmPasswordError && errors.confirmPassword}
          />
          <StyledDialogActions>
            <Button
              color="primary"
              type="submit"
              form="form-part-1"
              variant="contained"
              disabled={
                confirmPasswordError ||
                !values.password ||
                !values.confirmPassword ||
                passwordUpdateError ||
                passwordResetInProgress ||
                !hasResetToken
              }
            >
              {t("passwordReset.button.label")}
            </Button>
          </StyledDialogActions>
        </StyledForm>
      </StyledDialogContent>
    </>
  );
}

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingTop: theme.spacing(2.5),
}));

const StyledForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  margin: "auto",
  width: "250px",
  paddingBottom: theme.spacing(2),
}));

const StyledDialogContent = styled(DialogContent)({
  padding: "12px 32px !important",
});

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingTop: theme.spacing(2),
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(2),
  marginTop: theme.spacing(1),
}));
